import type { Priority, Task } from "@app/core";
import { deadlineUrgencyLevel, fromIso, startOfDay } from "@app/core";
import { Button, Calendar, Clock, CornerDownRight, Flag, MapPin, Repeat, Sun, Target, cn } from "@app/ui";
import { useMemo } from "react";

import { motion, springs, useReducedMotion } from "../../lib/motion/index.js";
import { useAppLocale } from "../../lib/useAppLocale.js";
import { useI18n } from "../../lib/useI18n.js";
import { useTimeFormat } from "../../lib/useTimeFormat.js";
import { useTasksStore, useTopicsStore } from "../../stores/index.js";
import { useEffectivePriority, useEffectivePriorityCtx } from "../priority-matrix/useEffectiveMatrixDisplay.js";
import { PRIORITY_FLAG_HEX } from "../task-colors.js";
import { TaskCheck } from "./TaskCheck.js";
import { overdueAgeDays } from "./agenda.js";
import { useTaskAccent } from "./useTaskAccent.js";

interface Props {
  task: Task;
  /** "overdue" rows show how late + the original stamp; "day" rows a time chip. */
  variant?: "day" | "overdue";
  /** Needed by the overdue variant for the "N d overdue" badge. */
  now?: Date;
  /** Fully-past open slot (today only) — the row renders at reduced opacity. */
  dimmed?: boolean;
  /** Unique key for keyboard navigation (`data-agenda-row`); also marks the
   *  row focused when it matches `focusedKey`. */
  navKey?: string | undefined;
  focusedKey?: string | null | undefined;
  onToggle: (task: Task, done: boolean) => void;
  onEdit: (task: Task) => void;
  onView: (task: Task) => void;
  /** When set, a quick-snooze (clock) action opens the SnoozeMenu at the click. */
  onSnooze?: ((task: Task, pos: { x: number; y: number }) => void) | undefined;
  /** When set, a sun action opens the move-to-today TimeLadder at the click. */
  onMoveToToday?: ((task: Task, pos: { x: number; y: number }) => void) | undefined;
  /** When set (and the task is a movable one-off), the time chip becomes a
   *  button opening the TimeLadder to reschedule within the row's own day. */
  onReschedule?: ((task: Task, pos: { x: number; y: number }) => void) | undefined;
}

/** How the deadline pill tints per derived urgency: alarming only when near. */
const DEADLINE_TONE: Record<Priority, string> = {
  high: "bg-red-500/15 text-red-600 dark:text-red-400",
  medium: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  low: "bg-muted text-muted-foreground",
  none: "bg-muted text-muted-foreground",
};

/**
 * One agenda line: task-colored accent bar + animated completion disc (plain
 * dot for events), title with live priority flag + repeat marker, a meta line
 * (topic emojis, deadline pill, subtask roll-up, location, notes), a tinted
 * time chip (or overdue badge), and hover-revealed quick actions (snooze,
 * move-to-today, jump-to-calendar). Fades/collapses out when it leaves.
 */
export function AgendaRow({
  task: t, variant = "day", now, dimmed = false, navKey, focusedKey,
  onToggle, onEdit, onView, onSnooze, onMoveToToday, onReschedule,
}: Props) {
  const { t: tr } = useI18n();
  const tf = useTimeFormat();
  const locale = useAppLocale();
  const reduce = useReducedMotion();
  const accent = useTaskAccent(t);
  const priorityOf = useEffectivePriority();
  const ctx = useEffectivePriorityCtx();
  const allTasks = useTasksStore((s) => s.tasks);
  const topics = useTopicsStore((s) => s.topics);
  const done = t.status === "done";
  const overdue = variant === "overdue";

  // Live (matrix/deadline-derived) priority — same resolver as the calendar
  // blocks and the priority filter, so the flag can never disagree with them.
  const level = priorityOf(t);

  const deadlineLevel = t.deadline_at
    ? deadlineUrgencyLevel(t.deadline_at, ctx.now, t.tz ?? ctx.defaultTz, ctx.config)
    : "none";

  // Subtasks hang off the REAL series id — a virtual occurrence's children
  // live on its source task.
  const sourceId = t.virtual ? (t.parent_id ?? t.id) : t.id;
  const subs = useMemo(
    () => allTasks.filter((c) => !c.deleted_at && c.subtask_parent_id === sourceId),
    [allTasks, sourceId],
  );
  const subsDone = subs.filter((c) => c.status === "done").length;

  const topicIcons = useMemo(
    () => (t.topic_ids ?? [])
      .map((id) => topics.find((tp) => tp.id === id))
      .filter((tp): tp is NonNullable<typeof tp> => !!tp?.icon)
      .slice(0, 3),
    [t.topic_ids, topics],
  );

  const hasMeta = overdue || topicIcons.length > 0 || !!t.deadline_at || subs.length > 0 || t.location || t.notes;
  const focused = !!navKey && focusedKey === navKey;
  // Series/occurrence moves belong to the calendar's this/following/all flow;
  // the quick chip only reschedules movable one-offs.
  const movable = !done && !t.virtual && !t.recurrence_id;

  return (
    <motion.li
      data-task-id={t.id}
      data-agenda-row={navKey}
      // Enter/exit shape shared with the Priorities QueueRow: fade/slide the
      // row itself, siblings close the gap via `layout`.
      layout={!reduce}
      initial={reduce ? false : { opacity: 0, y: -6 }}
      animate={{ opacity: dimmed && !done ? 0.55 : 1, y: 0 }}
      exit={reduce ? { opacity: 0, transition: { duration: 0 } } : { opacity: 0, x: -16 }}
      transition={reduce ? { duration: 0 } : springs.smooth}
      className={cn(
        "group relative grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 py-2 pl-4 pr-2 transition-colors hover:bg-accent/40 sm:flex sm:gap-3",
        focused && "bg-accent/40 ring-2 ring-inset ring-primary/50",
      )}
    >
      <span
        aria-hidden
        className="absolute inset-y-1.5 left-1 w-[3px] rounded-full transition-opacity"
        style={{ background: accent, opacity: done ? 0.35 : 0.9 }}
      />
      {t.is_event ? (
        <span className="grid h-[18px] w-[18px] shrink-0 place-items-center" aria-hidden>
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: accent }} />
        </span>
      ) : (
        <TaskCheck
          done={done}
          accent={accent}
          label={done ? tr("agenda.markNotDone") : tr("agenda.markDone")}
          onToggle={(next) => onToggle(t, next)}
        />
      )}

      <div
        role="button"
        tabIndex={0}
        onClick={() => onEdit(t)}
        onKeyDown={(e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          e.preventDefault();
          onEdit(t);
        }}
        title={tr("agenda.clickToEdit")}
        className="flex min-w-0 flex-1 cursor-pointer flex-col gap-0.5 rounded-md text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <div className="flex min-w-0 items-start gap-1.5">
          <span className={cn("min-w-0 break-words whitespace-normal sm:truncate", done && "text-muted-foreground line-through")}>
            {t.title || <em className="text-muted-foreground">{tr("agenda.untitled")}</em>}
          </span>
          {level !== "none" && !done && (
            <Flag
              className="h-3 w-3 shrink-0"
              fill={PRIORITY_FLAG_HEX[level]}
              stroke={PRIORITY_FLAG_HEX[level]}
              aria-label={tr(`sidebar.priority.${level}`)}
            />
          )}
          {t.recurrence_id && <Repeat className="h-3 w-3 shrink-0 text-muted-foreground" aria-label={tr("agenda.recurring")} />}
        </div>
        {hasMeta && (
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground sm:flex-nowrap">
            {overdue && t.due_at && <span className="shrink-0 tabular-nums">{tf.stamp(t.due_at)}</span>}
            {topicIcons.length > 0 && (
              <span className="shrink-0" title={topicIcons.map((tp) => tp.name).join(" · ")} aria-hidden>
                {topicIcons.map((tp) => tp.icon).join(" ")}
              </span>
            )}
            {t.deadline_at && (
              <span
                className={cn("flex shrink-0 items-center gap-0.5 rounded-md px-1 py-px text-[calc(11px*var(--app-text-scale))] font-medium", DEADLINE_TONE[deadlineLevel])}
                title={tr("today.deadline", { date: tf.stamp(t.deadline_at) })}
              >
                <Target className="h-3 w-3" aria-hidden />
                {deadlineDayLabel(t.deadline_at, now ?? ctx.now, locale, tr)}
              </span>
            )}
            {subs.length > 0 && (
              <span
                className="flex shrink-0 items-center gap-0.5 tabular-nums"
                title={tr("today.subtasks", { done: subsDone, total: subs.length })}
              >
                <CornerDownRight className="h-3 w-3" aria-hidden />
                {subsDone}/{subs.length}
              </span>
            )}
            {t.location && (
              <span className="flex min-w-0 max-w-full items-center gap-0.5">
                <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                <span className="break-words sm:truncate">{t.location}</span>
              </span>
            )}
            {t.notes && <span className="min-w-0 max-w-full break-words sm:truncate">{t.notes}</span>}
          </div>
        )}
      </div>

      {overdue ? (
        now && t.due_at && <OverdueBadge task={t} now={now} />
      ) : (
        t.due_at && (onReschedule && movable ? (
          <button
            type="button"
            className="shrink-0 cursor-pointer rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums transition-shadow hover:ring-1 hover:ring-inset hover:ring-foreground/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            style={{ background: `${accent}1f` }}
            title={tr("today.rescheduleChip")}
            onClick={(e) => {
              e.stopPropagation();
              onReschedule(t, actionAnchor(e));
            }}
          >
            {t.all_day ? tr("agenda.allDay") : tf.hmDate(new Date(t.due_at))}
          </button>
        ) : (
          <span
            className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium tabular-nums", done && "opacity-60")}
            style={{ background: `${accent}1f` }}
          >
            {t.all_day ? tr("agenda.allDay") : tf.hmDate(new Date(t.due_at))}
          </span>
        ))
      )}

      <div className="col-start-2 col-end-4 flex shrink-0 items-center justify-self-end gap-0.5 transition-opacity group-focus-within:opacity-100 sm:col-auto sm:justify-self-auto [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100">
        {onSnooze && !done && !t.is_event && (
          <RowAction title={tr("agenda.snoozeTitle")} onClick={(pos) => onSnooze(t, pos)}>
            <Clock className="h-4 w-4" />
          </RowAction>
        )}
        {onMoveToToday && !done && !t.is_event && (
          <RowAction title={tr("today.moveToToday")} onClick={(pos) => onMoveToToday(t, pos)}>
            <Sun className="h-4 w-4" />
          </RowAction>
        )}
        {t.due_at && (
          <RowAction title={tr("agenda.showOnCalendar")} onClick={() => onView(t)}>
            <Calendar className="h-4 w-4" />
          </RowAction>
        )}
      </div>
    </motion.li>
  );
}

/** Deadline pill text: Today/Tomorrow, a weekday inside the next week, else a
 *  short date. Past deadlines show the date — the tint already yells. */
function deadlineDayLabel(
  iso: string,
  now: Date,
  locale: string,
  tr: (key: "today.relative.today" | "today.relative.tomorrow") => string,
): string {
  const d = fromIso(iso);
  const diff = Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / 86_400_000);
  if (diff === 0) return tr("today.relative.today");
  if (diff === 1) return tr("today.relative.tomorrow");
  if (diff > 1 && diff < 7) return d.toLocaleDateString(locale, { weekday: "short" });
  return d.toLocaleDateString(locale, { month: "short", day: "numeric" });
}

/** Red "how late" pill for the overdue section. */
function OverdueBadge({ task, now }: { task: Task; now: Date }) {
  const { t: tr } = useI18n();
  const days = overdueAgeDays(task, now);
  return (
    <span className="shrink-0 rounded-full bg-red-500/15 px-2 py-0.5 text-[calc(11px*var(--app-text-scale))] font-medium text-red-600 dark:text-red-400">
      {days <= 0 ? tr("today.overdueToday") : tr("today.overdueBy", { days })}
    </span>
  );
}

function RowAction({ title, onClick, children }: {
  title: string;
  onClick: (pos: { x: number; y: number }) => void;
  children: React.ReactNode;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground sm:h-7 sm:w-7"
      onClick={(e) => {
        e.stopPropagation();
        onClick(actionAnchor(e));
      }}
      title={title}
      aria-label={title}
    >
      {children}
    </Button>
  );
}

/** Keyboard/screen-reader clicks report (0,0); anchor their popover to the
 * control instead of opening it at the viewport's top-left corner. */
function actionAnchor(e: React.MouseEvent<HTMLButtonElement>): { x: number; y: number } {
  if (e.detail !== 0 && (e.clientX !== 0 || e.clientY !== 0)) {
    return { x: e.clientX, y: e.clientY };
  }
  const rect = e.currentTarget.getBoundingClientRect();
  return { x: rect.left + rect.width / 2, y: rect.bottom };
}
